import { AssetCreateTypeEnum } from "app/components/MediaUploader/types";
import { ITicketDescription } from "app/containers/Features/containers/Boards/containers/Tickets/types";
import type { CustomFieldItem } from "app/containers/Features/containers/TeamSettings/container/Projects/container/CustomFields/types";
import { IEffort } from "app/containers/Features/types";
import { IMetaData, TotalEffortType } from "app/types";


export type TicketActionItem = {
  id: string;
  isResolved: boolean;
  label: string;
};

export interface TicketActionItemsState {
  items: TicketActionItem[];
  newItemValue: string;
  editingItemId: string | null;
  editingValue: string;
  newInlineItemId: string | null;
}

export interface TicketAttachmentFile {
  id: string;
  name: string;
  type: AssetCreateTypeEnum;
  createdAt?: string;
  url?: string;
  filePath?: string;
}

export interface TicketAttachmentState {
  files: TicketAttachmentFile[];
  renamingFileId: string | null;
  renameValue: string;
  renameAttachmentLoading: boolean;
  showDeleteModal: boolean;
  fileToDeleteId: string | null;
  previewFileId: string | null;
  previewFileIds: string[] | null;
  isPreviewOpen: boolean;
  previewUrlLoading: boolean;
  textPreview: {
    fileUrl: string | null;
    content: string | null;
    isLoading: boolean;
    hasError: boolean;
  };
}

export type AttachmentPreviewActionHeaderProps = {
  fileName?: string;
  onDownload?: () => void;
  onOpenFile?: () => void;
  onCopyUrl?: () => void;
  onDelete?: () => void;
  onClose?: () => void;
  onRenameSubmit?: (name: string) => void;
  zoomLevel?: number;
  onZoomOut?: () => void;
  onZoomIn?: () => void;
  currentItemNumber?: number;
  totalItems?: number;
};

export enum DetailsTabs {
  activity = "activity",
  relations = "relations",
  subscribers = "subscribers",
  code = "code",
  info = "info",
}

export interface IEffortActual {
  _id: string;
  effort: IEffort;
  isLock: boolean;
  memberId: string;
}

export type EffortActual = {
  hours: number;
  minutes: number;
  date: string;
  startTime: string;
  endTime: string;
};

export type EffortActualFormType = {
  projectId: string;
  effort: EffortActual;
};

export interface TicketDetailsState {
  activeDetailsTab: DetailsTabs;
  details: {
    loading: boolean;
    result: ITicketDetails;
  };
  duplicateAction: {
    loading: boolean;
  };
  archiveAction: {
    loading: boolean;
  };
  changeTicketNameAction: {
    loading: boolean;
  };
  toggleEffortEstimateMenu: boolean;
  updateEffortEstimatedLoading: boolean;
  enableTicketDescription: boolean;
  enableAddNewTemplateForm: boolean;
  ticketDescriptionForm: ITicketDescriptionForm;
  updateTicketDescriptionLoading: boolean;
  actionItems: {
    items: TicketActionItem[];
    newItemValue: string;
    editingItemId: string | null;
    editingValue: string;
    newInlineItemId: string | null;
  };
  attachmentState: TicketAttachmentState;
}

export type IActionItem = {
  _id: string;
  createdAt: string;
  updatedAt: string;
  title: string;
  ticketId: string;
  createdBy: string;
  completed: boolean;
};

export type TimeSheetEffortRunning = {
  _id: string;
};

export type TimeSheetEffortCompleted = {
  hours: number;
  minutes: number;
  startTime: string;
  endTime: string;
  date: string;
  _id: string;
};

export type TimeSheetEntry = {
  _id: string;
  effort: TimeSheetEffortRunning | TimeSheetEffortCompleted;
  ticketId: string;
  isLock: boolean;
  billableHours: boolean;
  ticketTitle: string;
  ticketType: string;
};

export type TimeSheetGroup = {
  _id: string | null;
  data: TimeSheetEntry[];
  totalTime: { hours: number; minutes: number };
};

export interface ITicketDetails {
  _id: string;
  ticketID: string;
  ticketName: string;
  type: string;
  status: string;
  assignedTo: string;
  dueDate: string;
  priority: string;
  effortEstimated: IEffortActual[];
  effortActual: IEffortActual[];
  tags: string[];
  description: ITicketDescription;
  actionItems: IActionItem[];
  attachments: string[];
  customFields: CustomFieldItem[];
  release: string;
  sprint: string;
  epic: string;
  environment: string;
  createdBy: string;
  createdDate: string;
  file: unknown[];
  project: {
    _id: string;
    teamId: string;
    name: string;
    isArchived: boolean;
  };
  team: {
    _id: string;
    teamName: string;
  };
  totalEffortEstimated: TotalEffortType;
  totalEffortActual: TotalEffortType;
  isFavorite: 0 | 1;
}

export interface GetDetailsApiResponse {
  data: ITicketDetails;
}

export interface DuplicateApiResponse {
  statusCode: number;
  message: string;
  _metadata: Omit<IMetaData, "pagination">;
}

export interface ArchiveApiResponse {
  statusCode: number;
  message: string;
  _metadata: Omit<IMetaData, "pagination">;
}

export interface FavoriteApiResponse {
  statusCode: number;
  message: string;
  _metadata: Omit<IMetaData, "pagination">;
}

export interface ChangeTicketNameApiResponse {
  statusCode: number;
  message: string;
  _metadata: Omit<IMetaData, "pagination">;
}

export type CreateActionItemApiResponse = {
  statusCode: number;
  message: string;
  _metadata: Omit<IMetaData, "pagination">;
  data?: IActionItem;
};

export type UpdateActionItemApiResponse = {
  statusCode: number;
  message: string;
  _metadata: Omit<IMetaData, "pagination">;
};

export type ActionItemOrder = {
  before: string;
  after: string;
};

export enum TicketActionItemUpdateTypeEnum {
  RENAME = "rename",
  ORDER_UPDATE = "orderUpdate",
  COMPLETE_INCOMPLETE = "completeIncomplete",
  REMOVE = "remove",
}

export enum TicketActionItemBulkUpdateTypeEnum {
  COMPLETE_INCOMPLETE_BULK = "completeIncompleteBulk",
  REMOVE_BULK = "removeBulk",
}

export interface UpdateCustomFieldValueApiResponse {
  statusCode: number;
  message: string;
  _metadata: Omit<IMetaData, "pagination">;
}

export type ActionItemUpdateRequest = {
  id: string;
  projectId: string;
  title: string;
  updateType: TicketActionItemUpdateTypeEnum;
  completed?: boolean;
  order?: ActionItemOrder;
};

export type ActionItemBulkUpdateRequest = {
  id: string;
  projectId: string;
  updateType: TicketActionItemBulkUpdateTypeEnum;
  completed?: boolean;
};

export interface ITicketDescriptionData {
  content: string;
  rawContent: string;
  mentionedUserIds: string[];
  mentionedTicketIds: string[];
  attachments: string[];
}

export type ITicketDescriptionForm = {
  description: ITicketDescriptionData;
  projectId: string;
};
